import type Konva from 'konva';
import { useCallback } from 'react';
import { useCanvasStore } from '../store/canvasStore';
import useCanvasHistory from './useCanvasHistory';

const useTransformHandlers = () => {
  const textRefs = useCanvasStore((s) => s.textRefs);
  const logoRef = useCanvasStore((s) => s.logoRef);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const setLogoProps = useCanvasStore((s) => s.setLogoProps);
  const saveStateToHistory = useCanvasStore((s) => s.saveStateToHistory);

  const { saveHistory } = useCanvasHistory();

  const handleTextTransform = useCallback(
    (id: string): void => {
      saveStateToHistory();
      const textNode = textRefs.current.get(id);
      if (!textNode) return;

      const scaleX = textNode.scaleX();
      const width = Math.max(20, textNode.width() * scaleX);

      // Calculate new font size based on the scale
      const fontSize = Math.round(textNode.fontSize() * scaleX);

      textNode.width(width);
      textNode.scaleX(1);
      textNode.scaleY(1);

      setTexts((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                width,
                x: textNode.x(),
                y: textNode.y(),
                fontSize, // Update the font size to match the scaling
              }
            : t
        )
      );

      const layer = textNode.getLayer();
      if (layer) layer.batchDraw();
    },
    [saveStateToHistory, setTexts, textRefs]
  );

  const handleLogoTransform = useCallback((): void => {
    if (!logoRef.current) return;
    saveStateToHistory();

    const konvaLogo = logoRef.current as unknown as Konva.Image;

    const width = konvaLogo.width();
    const height = konvaLogo.height();
    const newWidth = width * konvaLogo.scaleX();
    const newHeight = height * konvaLogo.scaleY();
    const x = konvaLogo.x();
    const y = konvaLogo.y();

    konvaLogo.width(newWidth);
    konvaLogo.height(newHeight);
    konvaLogo.scaleX(1);
    konvaLogo.scaleY(1);

    setLogoProps((prev) => ({
      ...prev,
      x,
      y,
      width: newWidth,
      height: newHeight,
    }));

    const layer = konvaLogo.getLayer();
    if (layer) layer.batchDraw();
  }, [logoRef, setLogoProps, saveStateToHistory]);

  return {
    handleTextTransform,
    handleLogoTransform,
  };
};

export default useTransformHandlers;
